import { FC } from "react";
import { LinkIcon } from "lucide-react";

/**
 * Props for the CitationButton component
 */
interface Props {
  /** Callback function called when the button is clicked */
  onClick: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Button label text. Defaults to "Sources". */
  label?: string;
  /** Whether the button is in active/pressed state */
  isActive?: boolean;
  /** Whether sources are available. Button is hidden if false. */
  hasSources?: boolean;
}

/**
 * CitationButton component displays a button for opening citation sources.
 * Only renders when hasSources is true. Shows active state when isActive is true.
 * 
 * @component
 * @param {Props} props - Component props
 * @param {() => void} props.onClick - Callback fired when button is clicked
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.label="Sources"] - Button label text
 * @param {boolean} [props.isActive=false] - Whether button is in active state
 * @param {boolean} [props.hasSources=false] - Whether sources are available
 * @returns {JSX.Element | null} Button element or null if no sources available
 * 
 * @example
 * ```tsx
 * <CitationButton 
 *   onClick={handleOpenSources}
 *   hasSources={true}
 *   isActive={sourcesOpen}
 * />
 * ```
 */
export const CitationButton: FC<Props> = ({ 
  onClick, 
  className = "", 
  label = "Referenced Policies",
  isActive = false,
  hasSources = false 
}) => {
  if (!hasSources) {
    return null;
  }

  return (
    <button
      type="button"
      className={`flex items-center rounded-md pr-3 pl-1 transition-colors ${
        isActive 
          ? 'bg-[rgb(var(--icon-policy-gpt))] text-white hover:bg-[rgb(var(--icon-policy-gpt)/0.85)]' 
          : 'bg-[rgb(var(--icon-policy-gpt)/0.18)] hover:bg-[rgb(var(--icon-policy-gpt)/0.1)] text-[rgb(var(--icon-policy-gpt))]'
      } ${className}`}
      onClick={onClick}
      aria-label="Open citation sources"
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
        isActive ? 'text-white' : 'text-[rgb(var(--icon-policy-gpt))]'
      }`}>
        <LinkIcon size={14} />
      </div>
      <div className="flex items-center justify-center pr-2 font-primary">{label}</div>
    </button>
  );
};